import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Card,
  Select,
  Input,
  List,
  Timeline,
  Descriptions,
  Empty,
  Spin,
  Modal,
} from "antd";
import { ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import PageHeader from "./common/PageHeader";
import { notify, serverError } from "../utils/notify";
import {
  getTicket,
  setTicketStatus,
  assignTicket,
  addTicketNote,
  getUsers,
  getUser,
} from "../network/api";
import {
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TAG_COLOR,
  TICKET_PRIORITY_LABEL,
  TICKET_PRIORITY_TAG_COLOR,
  TICKET_TYPE_LABEL,
  TICKET_TYPE_TAG_COLOR,
  availableActions,
  NOTE_PROMPT_ACTIONS,
} from "../constants/tickets";
import { relativeTime } from "../constants/health";
import { rankOf, ROLES } from "../constants/roles";

const { Text } = Typography;
const { TextArea } = Input;

const SUPERVISOR_RANK = rankOf(ROLES.SUPERVISOR);

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState();
  const [assigning, setAssigning] = useState(false);

  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  // Controlled note-prompt modal (antd's static Modal.confirm does not render
  // under React 19, so we drive a real <Modal> instead).
  const [noteModal, setNoteModal] = useState({ open: false, action: null, note: "" });

  const fetchTicket = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getTicket(id);
      setTicket(response.ticket || null);
    } catch (error) {
      console.error("Error fetching ticket:", error);
      notify.error(serverError(error, "Failed to load ticket."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchCurrentUser = useCallback(async () => {
    const cached = localStorage.getItem("user");
    if (cached) {
      try {
        setCurrentUser(JSON.parse(cached) || null);
      } catch (e) {
        // ignore malformed cache
      }
    }
    try {
      const user = await getUser();
      setCurrentUser(user || null);
    } catch (error) {
      console.error("Error fetching current user:", error);
    }
  }, []);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const currentRole = currentUser?.role;
  const currentUserId = currentUser?._id;
  // Supervisors+ may assign tickets (full lifecycle control).
  const canManageTickets = rankOf(currentRole) >= SUPERVISOR_RANK;

  // Fetch technicians for the assignee picker once we know the actor can assign.
  const fetchTechnicians = useCallback(async () => {
    if (!canManageTickets) return;
    try {
      const response = await getUsers({ role: ROLES.TECHNICIAN });
      const options = (response.users || []).map((u) => ({
        value: u._id,
        label: u.name,
      }));
      setTechnicians(options);
    } catch (error) {
      console.error("Error fetching technicians:", error);
    }
  }, [canManageTickets]);

  useEffect(() => {
    fetchTechnicians();
  }, [fetchTechnicians]);

  const isAssignee =
    !!ticket?.assignedTo?._id && ticket.assignedTo._id === currentUserId;

  const actions = ticket
    ? availableActions(ticket.status, currentRole, isAssignee)
    : [];

  // "Assign" / "Reassign" are surfaced as the dedicated assignee control rather
  // than a plain status button, so drop them from the action bar.
  const barActions = actions.filter(
    (a) => a.key !== "assign" && a.key !== "reassign"
  );

  const runStatusTransition = async (action, note) => {
    setActing(true);
    try {
      await setTicketStatus(ticket._id, action.toStatus, note);
      notify.success(`Ticket marked ${action.label}.`);
      await fetchTicket();
    } catch (error) {
      console.error("Error updating ticket status:", error);
      notify.error(serverError(error, "Failed to update ticket status."));
    } finally {
      setActing(false);
    }
  };

  const handleAction = (action) => {
    if (NOTE_PROMPT_ACTIONS.has(action.key)) {
      setNoteModal({ open: true, action, note: "" });
      return;
    }
    runStatusTransition(action, undefined);
  };

  const submitNoteModal = async () => {
    const { action, note } = noteModal;
    setNoteModal({ open: false, action: null, note: "" });
    await runStatusTransition(action, note || undefined);
  };

  const handleAssign = async () => {
    if (!selectedTech) {
      notify.warning("Select a technician first.");
      return;
    }
    setAssigning(true);
    try {
      await assignTicket(ticket._id, selectedTech);
      notify.success("Ticket assigned.");
      setSelectedTech(undefined);
      await fetchTicket();
    } catch (error) {
      console.error("Error assigning ticket:", error);
      notify.error(serverError(error, "Failed to assign ticket."));
    } finally {
      setAssigning(false);
    }
  };

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setAddingNote(true);
    try {
      await addTicketNote(ticket._id, text);
      setNoteText("");
      await fetchTicket();
    } catch (error) {
      console.error("Error adding note:", error);
      notify.error(serverError(error, "Failed to add note."));
    } finally {
      setAddingNote(false);
    }
  };

  // supervisor+ may assign on open, reassign on assigned/in_progress.
  const canAssign =
    canManageTickets &&
    ticket &&
    ["open", "assigned", "in_progress"].includes(ticket.status);

  if (!loading && !ticket) {
    return (
      <div>
        <Space style={{ marginBottom: 12 }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/dashboard/tickets")}
            style={{ paddingLeft: 0 }}
          >
            Back to Tickets
          </Button>
        </Space>
        <Empty description="Ticket not found" />
      </div>
    );
  }

  const status = ticket?.status;
  const notes = ticket?.notes || [];
  const history = ticket?.history || [];

  return (
    <Spin spinning={loading}>
      <div>
        <Space
          style={{ width: "100%", justifyContent: "space-between" }}
          align="center"
        >
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/dashboard/tickets")}
            style={{ paddingLeft: 0 }}
          >
            Back to Tickets
          </Button>
          <Button icon={<ReloadOutlined />} onClick={fetchTicket}>
            Refresh
          </Button>
        </Space>

        {/* Header */}
        <div style={{ marginTop: 8 }}>
          <PageHeader
            title={
              <Space align="center" size="middle" wrap>
                {ticket?.title}
                {status && (
                  <Tag
                    color={TICKET_STATUS_TAG_COLOR[status]}
                    style={{ fontSize: 14, padding: "2px 10px" }}
                  >
                    {TICKET_STATUS_LABEL[status] || status}
                  </Tag>
                )}
                {ticket?.priority && (
                  <Tag color={TICKET_PRIORITY_TAG_COLOR[ticket.priority]}>
                    {TICKET_PRIORITY_LABEL[ticket.priority] || ticket.priority}
                  </Tag>
                )}
                {ticket?.type && (
                  <Tag color={TICKET_TYPE_TAG_COLOR[ticket.type]}>
                    {TICKET_TYPE_LABEL[ticket.type] || ticket.type}
                  </Tag>
                )}
              </Space>
            }
            subtitle={
              <span>
                Device:{" "}
                {ticket?.deviceId ? (
                  <Button
                    type="link"
                    style={{ padding: 0, height: "auto", fontSize: 13 }}
                    onClick={() =>
                      navigate(`/dashboard/health/${ticket.deviceId}`)
                    }
                  >
                    {ticket.deviceId}
                  </Button>
                ) : (
                  "—"
                )}
              </span>
            }
          />
        </div>

        {/* Action bar */}
        <Card style={{ marginTop: 16 }} size="small">
          <Space wrap>
            {barActions.length === 0 ? (
              <Text type="secondary">No lifecycle actions available.</Text>
            ) : (
              barActions.map((action) => (
                <Button
                  key={action.key}
                  type={action.key === "reopen" ? "default" : "primary"}
                  danger={action.key === "close"}
                  loading={acting}
                  onClick={() => handleAction(action)}
                >
                  {action.label}
                </Button>
              ))
            )}
          </Space>

          {/* Assign / Reassign control (hardwareAdmin+) */}
          {canAssign && (
            <div style={{ marginTop: 12 }}>
              <Space wrap>
                <Text type="secondary">
                  {ticket?.assignedTo ? "Reassign to" : "Assign to"}:
                </Text>
                <Select
                  placeholder="Select technician"
                  style={{ width: 240 }}
                  value={selectedTech}
                  options={technicians}
                  onChange={setSelectedTech}
                  showSearch
                  optionFilterProp="label"
                />
                <Button
                  type="primary"
                  loading={assigning}
                  onClick={handleAssign}
                >
                  {ticket?.assignedTo ? "Reassign" : "Assign"}
                </Button>
              </Space>
            </div>
          )}
        </Card>

        <Row gutter={16} style={{ marginTop: 16 }}>
          {/* Details + notes */}
          <Col xs={24} lg={14}>
            <Card title="Details">
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Description">
                  {ticket?.description || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Assignee">
                  {ticket?.assignedTo?.name || "Unassigned"}
                </Descriptions.Item>
                <Descriptions.Item label="Created by">
                  {ticket?.createdBy?.name || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Verified by">
                  {ticket?.verifiedBy?.name || "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Opened">
                  {relativeTime(ticket?.openedAt)}
                </Descriptions.Item>
                {ticket?.resolvedAt && (
                  <Descriptions.Item label="Resolved">
                    {relativeTime(ticket.resolvedAt)}
                  </Descriptions.Item>
                )}
                {ticket?.resolutionDurationMins != null && (
                  <Descriptions.Item label="Resolution Time">
                    {ticket.resolutionDurationMins} min
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Card title="Notes" style={{ marginTop: 16 }}>
              {notes.length === 0 ? (
                <Empty description="No notes yet" />
              ) : (
                <List
                  size="small"
                  dataSource={notes}
                  renderItem={(n) => (
                    <List.Item>
                      <List.Item.Meta
                        title={n.byName}
                        description={
                          <span>
                            {n.text}
                            <Text
                              type="secondary"
                              style={{ marginLeft: 8, fontSize: 12 }}
                            >
                              {relativeTime(n.at)}
                            </Text>
                          </span>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
              <Space.Compact style={{ width: "100%", marginTop: 12 }}>
                <Input
                  placeholder="Add a note"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onPressEnter={handleAddNote}
                />
                <Button
                  type="primary"
                  loading={addingNote}
                  onClick={handleAddNote}
                >
                  Add
                </Button>
              </Space.Compact>
            </Card>
          </Col>

          {/* History timeline */}
          <Col xs={24} lg={10}>
            <Card title="History">
              {history.length === 0 ? (
                <Empty description="No history yet" />
              ) : (
                <Timeline
                  items={history.map((h, idx) => ({
                    key: idx,
                    color:
                      TICKET_STATUS_TAG_COLOR[h.to] === "red"
                        ? "red"
                        : TICKET_STATUS_TAG_COLOR[h.to] === "green"
                        ? "green"
                        : "blue",
                    children: (
                      <div>
                        <Space size={4} wrap>
                          {h.from && (
                            <Tag color={TICKET_STATUS_TAG_COLOR[h.from]}>
                              {TICKET_STATUS_LABEL[h.from] || h.from}
                            </Tag>
                          )}
                          <span>→</span>
                          <Tag color={TICKET_STATUS_TAG_COLOR[h.to]}>
                            {TICKET_STATUS_LABEL[h.to] || h.to}
                          </Tag>
                        </Space>
                        <div>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {h.byName} · {relativeTime(h.at)}
                          </Text>
                        </div>
                        {h.note && <div>{h.note}</div>}
                      </div>
                    ),
                  }))}
                />
              )}
            </Card>
          </Col>
        </Row>

        <Modal
          open={noteModal.open}
          title={noteModal.action ? `${noteModal.action.label} ticket` : ""}
          okText={noteModal.action ? noteModal.action.label : "OK"}
          confirmLoading={acting}
          onOk={submitNoteModal}
          onCancel={() => setNoteModal({ open: false, action: null, note: "" })}
        >
          <TextArea
            rows={3}
            placeholder="Optional note"
            value={noteModal.note}
            onChange={(e) =>
              setNoteModal((m) => ({ ...m, note: e.target.value }))
            }
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default TicketDetail;
